/**
 * Active dashboard workspace (PRIVATE fork).
 *
 * The dashboard talks to letpot-api through /api/dashboard, which requires an
 * X-Workspace header naming a bound workspace (see resolveProxy). This module
 * holds the currently-selected workspace (localStorage) and a fetch wrapper that
 * injects the header — passed to the CRUD data source as its fetcher.
 */

import { useSyncExternalStore } from 'react';
import { authFetch } from '../auth';

const KEY = 'dashboard-active-workspace';

let active = readInitial();
const listeners = new Set<() => void>();

function readInitial(): string {
  try {
    return localStorage.getItem(KEY) ?? '';
  } catch {
    return '';
  }
}

export function getActiveWorkspace(): string {
  return active;
}

export function setActiveWorkspace(id: string): void {
  active = id;
  try {
    localStorage.setItem(KEY, id);
  } catch {
    /* ignore */
  }
  listeners.forEach((l) => l());
}

export function useActiveWorkspace(): string {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    getActiveWorkspace,
    getActiveWorkspace,
  );
}

/** authFetch + the X-Workspace header. Used as the CRUD data source's fetcher. */
export function dashboardFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (active) headers.set('X-Workspace', active);
  return authFetch(input, { ...init, headers });
}
